import { useQuery } from '@tanstack/react-query';
import { companiesService } from '@/services/companies-service';
import { mockCompanies } from '@/lib/mock-data';
import { getToken, useAuth } from '@certara/common-react-components';

/**
 * Dev-mode bypass: when the backend (or test harness) injects the sentinel
 * `dev-mock-token`, we short-circuit network calls and return mock data.
 */
const DEV_MOCK_TOKEN = 'dev-mock-token';

export function useCompanies() {
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;

  const isDevelopment = import.meta.env.DEV;
  const useDevMode = isDevelopment && window.oktaToken === DEV_MOCK_TOKEN;

  return useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      if (useDevMode) {
        return mockCompanies;
      }

      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      const hasToken = await getToken();
      if (!hasToken) {
        throw new Error('No access token available');
      }

      return companiesService.getCompanies();
    },
    enabled: isAuthenticated || useDevMode,
    retry: (failureCount, error) => {
      if (
        error?.message?.includes('authenticated') ||
        error?.message?.includes('token')
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
